import { useEffect, useMemo, lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useLocation } from "react-router-dom";
import { useCategories } from "@/hooks/useCategories";
import { useMatches } from "@/hooks/useMatches";
import { useCategoryLandingSettings } from "@/hooks/useSiteSettings";
import { Skeleton } from "@/components/ui/skeleton";
import { useLogoSettings } from "@/hooks/useLogoSettings";
import { useViewerTracking } from "@/hooks/useViewerTracking";
import { useClickTracking } from "@/hooks/useClickTracking";

const StreamHubLayout = lazy(() => import("@/components/landing/StreamHubLayout"));

const CategoryGateway = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: categories = [], isLoading: catLoading } = useCategories();
  const { data: matches = [] } = useMatches();
  const { data: logoSettings } = useLogoSettings();
  const { data: landingSettings, isLoading: landingLoading } = useCategoryLandingSettings();

  const searchParams = new URLSearchParams(location.search);
  const refCode = searchParams.get("ref");

  // Track page visit (viewer tracking for online/offline presence)
  useViewerTracking({ pageVisited: '/' });

  // Auto-track click on page load with affiliate code if present
  useClickTracking({
    pageUrl: '/',
    affiliateCode: refCode || undefined,
  });

  const logoPath = logoSettings?.logo_path || "/images/golden-stream-logo.png";
  const isEnabled = landingSettings?.landing_page_enabled ?? false;
  const displayMode = landingSettings?.landing_display_mode ?? 'logo';
  const layoutStyle = landingSettings?.landing_page_layout ?? 'classic';

  // Bypass detection: affiliate links (/a/*), direct category links, query params
  const hasRef = searchParams.has("ref");
  const hasCategoryParam = searchParams.has("category");

  // If landing page is OFF or user arrived via affiliate/direct link, go to homepage
  useEffect(() => {
    if (landingLoading) return;

    if (!isEnabled || hasRef || hasCategoryParam) {
      navigate("/home", { replace: true });
      return;
    }
  }, [isEnabled, landingLoading, hasRef, hasCategoryParam, navigate]);

  // Count live matches per category
  const liveCountByCategory = useMemo(() => {
    const map: Record<string, number> = {};
    matches.forEach((m) => {
      if (m.is_live) {
        map[m.category_id] = (map[m.category_id] || 0) + 1;
      }
    });
    return map;
  }, [matches]);

  const handleSelect = (slug: string) => {
    navigate(`/category/${slug}`);
  };

  // Show nothing while checking settings
  if (landingLoading || !isEnabled) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // Stream Hub layout
  if (layoutStyle === 'stream_hub') {
    return (
      <>
        <Helmet>
          <title>Golden Stream - Live Sports Streams</title>
          <meta name="description" content="Watch live sports streams. Football, Basketball, Cricket, UFC and more." />
        </Helmet>
        <Suspense fallback={<div className="min-h-screen bg-background flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Loading...</div></div>}>
          <StreamHubLayout />
        </Suspense>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Golden Stream - Choose Your Sport</title>
        <meta
          name="description"
          content="Choose your favorite sports category to watch live streams. Football, Basketball, Cricket, UFC and more."
        />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col items-center justify-center relative overflow-hidden px-4 py-8">
        {/* Ambient glow effects */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-primary/5 blur-[120px] pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-primary/8 blur-[100px] pointer-events-none" />

        {/* Header: Logo or Site Name based on admin setting */}
        <div className="mb-6 md:mb-10">
          {displayMode === 'logo' ? (
            <img
              src={logoPath}
              alt="Golden Stream"
              className="h-12 md:h-16 object-contain"
              loading="eager"
            />
          ) : (
            <h2 className="text-3xl md:text-5xl font-display font-bold text-primary tracking-wide">
              Golden Stream
            </h2>
          )}
        </div>

        {/* Heading */}
        <h1 className="text-2xl md:text-4xl font-display text-foreground text-center mb-2 tracking-wide">
          Choose Your Sport
        </h1>
        <p className="text-muted-foreground text-center mb-8 md:mb-12 text-sm md:text-base max-w-md">
          Select a category to start watching live streams
        </p>

        {/* Category Grid */}
        <div className="w-full max-w-4xl">
          {catLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton
                  key={i}
                  className="aspect-square rounded-[18px] bg-card"
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {categories.map((cat) => {
                const liveCount = liveCountByCategory[cat.id] || 0;
                return (
                  <button
                    key={cat.id}
                    onClick={() => handleSelect(cat.slug)}
                    aria-label={`Browse ${cat.name} streams`}
                    className="group relative flex flex-col items-center justify-center aspect-square rounded-[18px] border border-border/50 bg-card/60 backdrop-blur-md cursor-pointer transition-all duration-300 hover:scale-[1.04] hover:border-primary/40 hover:shadow-[0_0_30px_hsl(var(--primary)/0.15)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  >
                    {/* Soft inner glow on hover */}
                    <div className="absolute inset-0 rounded-[18px] bg-gradient-to-b from-primary/0 to-primary/0 group-hover:from-primary/5 group-hover:to-transparent transition-all duration-300 pointer-events-none" />

                    {/* Live badge */}
                    {liveCount > 0 && (
                      <span className="absolute top-3 right-3 flex items-center gap-1 bg-primary/90 text-primary-foreground text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-foreground animate-pulse" />
                        {liveCount} Live
                      </span>
                    )}

                    {/* Icon: logo_url takes priority over emoji */}
                    {cat.logo_url ? (
                      <img
                        src={cat.logo_url}
                        alt={cat.name}
                        className="w-12 h-12 md:w-14 md:h-14 object-contain mb-3 transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-1"
                        loading="lazy"
                      />
                    ) : (
                      <span className="text-4xl md:text-5xl mb-3 transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-1">
                        {cat.icon || "🏆"}
                      </span>
                    )}

                    {/* Name */}
                    <span className="text-sm md:text-base font-semibold text-foreground group-hover:text-primary transition-colors duration-300">
                      {cat.name}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer note */}
        <p className="mt-10 text-muted-foreground/60 text-xs text-center">
          © {new Date().getFullYear()} Golden Stream. All rights reserved.
        </p>
      </div>
    </>
  );
};

export default CategoryGateway;
